import React from 'react';
import './BoothRulesPanel.css';

const BoothRulesPanel = ({ 
  violations = [], 
  suggestions = [], 
  onApplySuggestions, 
  onRemoveBooths, // new prop: remove selected booths tied to a violation (e.g., abandon group)
  onDismiss,
  selectedCount = 0,
  show = false 
}) => {
  if (!show || (violations.length === 0 && suggestions.length === 0)) {
    return null;
  }

  const errorViolations = violations.filter(v => v.severity === 'ERROR');
  const warningViolations = violations.filter(v => v.severity === 'WARNING');
  const highPrioritySuggestions = suggestions.filter(s => s.priority === 'HIGH');

  const handleApplyAllSuggestions = () => {
    if (onApplySuggestions) {
      onApplySuggestions(suggestions);
    }
  };

  const handleApplyHighPriority = () => {
    if (onApplySuggestions) {
      onApplySuggestions(highPrioritySuggestions);
    }
  };

  // Derive removable booth sets for incomplete group violations (all-or-none groups user wants to abandon)
  const incompleteGroupViolations = errorViolations.filter(v => v.type === 'INCOMPLETE_GROUP' && v.group && Array.isArray(v.group.booths));
  const removableGroupSelections = incompleteGroupViolations.map(v => ({
    groupName: v.group.name,
    boothIds: (v.group.booths || []).filter(id => suggestions.every(s => s.boothId !== id)) // avoid removing booths we are about to add
  })).filter(entry => entry.boothIds.length > 0);

  const handleRemoveGroupSelections = () => {
    if (!onRemoveBooths) return;
    // Flatten all booth ids from incomplete groups currently selected
    const toRemove = incompleteGroupViolations
      .flatMap(v => (v.group.booths || []).filter(id => v.selectedCount > 0))
      .filter((val, idx, arr) => arr.indexOf(val) === idx); // dedupe
    if (toRemove.length > 0) {
      onRemoveBooths(toRemove);
    }
  };

  // Derive removable booth sets for adjacent corner pairs requirement violations
  const adjacentCornerViolations = errorViolations.filter(v => v.type === 'ADJACENT_CORNERS_REQUIREMENT' && Array.isArray(v.cornerBooths));
  const handleRemoveAdjacentCornerSelections = () => {
    if (!onRemoveBooths) return;
    const toRemove = adjacentCornerViolations
      .flatMap(v => (v.cornerBooths || []).map(b => b && b.id).filter(Boolean))
      .filter((val, idx, arr) => arr.indexOf(val) === idx);
    if (toRemove.length > 0) {
      onRemoveBooths(toRemove);
    }
  };

  return (
    <div className="booth-rules-panel">
      <div className="rules-panel-header">
        <h4>
          <span className="rules-icon">⚠️</span>
          Booth Selection Rules
        </h4>
        <button className="dismiss-btn" onClick={onDismiss} title="Dismiss">
          ×
        </button>
      </div>

      <div className="rules-panel-content">
        {/* Current Selection Summary */}
        <div className="selection-summary">
          <span className="selection-count">
            {selectedCount} booth{selectedCount !== 1 ? 's' : ''} selected
          </span>
        </div>

        {/* Error Violations */}
        {errorViolations.length > 0 && (
          <div className="violations-section error-violations">
            <h5>
              <span className="error-icon">🚨</span>
              Required Actions ({errorViolations.length})
            </h5>
            <div className="violations-list">
              {errorViolations.map((violation, index) => (
                <div key={index} className="violation-item error">
                  <div className="violation-message">
                    {violation.message}
                  </div>
                  {violation.booth && (
                    <div className="violation-details">
                      Booth #{violation.booth.number} (Row {violation.booth.row}, Col {violation.booth.col})
                    </div>
                  )}
                  {violation.group && (
                    <div className="violation-details">
                      {violation.selectedCount}/{violation.requiredCount} booths selected in {violation.group.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning Violations */}
        {warningViolations.length > 0 && (
          <div className="violations-section warning-violations">
            <h5>
              <span className="warning-icon">⚠️</span>
              Recommendations ({warningViolations.length})
            </h5>
            <div className="violations-list">
              {warningViolations.map((violation, index) => (
                <div key={index} className="violation-item warning">
                  <div className="violation-message">
                    {violation.message}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Simplified Suggestions & Removal Actions */}
        {(highPrioritySuggestions.length > 0 || suggestions.length > 0 || incompleteGroupViolations.length > 0) && (
          <div className="suggestions-section">
            <h5>
              <span className="suggestion-icon">💡</span>
              Actions
            </h5>
            <div className="suggestions-actions compact">
              {highPrioritySuggestions.length > 0 && (
                <button 
                  className="apply-suggestions-btn primary"
                  onClick={handleApplyHighPriority}
                >
                  Add Required Booths ({highPrioritySuggestions.length})
                </button>
              )}
              {suggestions.length > highPrioritySuggestions.length && (
                <button 
                  className="apply-suggestions-btn secondary"
                  onClick={handleApplyAllSuggestions}
                >
                  Add All Suggestions ({suggestions.length})
                </button>
              )}
              {incompleteGroupViolations.length > 0 && (
                <button
                  className="apply-suggestions-btn danger-light"
                  onClick={handleRemoveGroupSelections}
                  title="Remove currently selected booths from incomplete group(s)"
                >
                  Remove Selected Group Booths
                </button>
              )}
              {adjacentCornerViolations.length > 0 && (
                <button
                  className="apply-suggestions-btn danger-light"
                  onClick={handleRemoveAdjacentCornerSelections}
                  title="Remove selected adjacent corner booth pair(s)"
                >
                  Remove Adjacent Corner Booths
                </button>
              )}
            </div>
            {incompleteGroupViolations.length > 0 && (
              <div className="removal-hint">
                <small>Don’t want this group? Use removal to clear your partial selection.</small>
              </div>
            )}
          </div>
        )}

        {/* Help Text */}
        <div className="rules-help minimal">
          <div className="help-text">
            <small>Resolve errors by adding required booths or remove partial group selections.</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothRulesPanel;